import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Layers, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { fetchGroups, fetchPermissionsMap, linkGroup } from '@/lib/traccar-api';

interface BulkAddGroupDialogProps {
  userIds: number[];
  disabled?: boolean;
}

export function BulkAddGroupDialog({ userIds, disabled }: BulkAddGroupDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [progress, setProgress] = useState<{ groupId: number; done: number; total: number } | null>(null);

  const { data: allGroups = [], isLoading: loadingGroups } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
    enabled: open,
  });

  const { data: permsMap, isLoading: loadingPerms } = useQuery({
    queryKey: ['permissions-map'],
    queryFn: fetchPermissionsMap,
    enabled: open,
  });

  const filteredGroups = allGroups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

  const handleAdd = async (groupId: number) => {
    // Skip users that already have this group
    const targets = userIds.filter(id => !(permsMap?.groupsByUser[id] ?? []).includes(groupId));
    const skipped = userIds.length - targets.length;
    setProgress({ groupId, done: 0, total: targets.length });
    let succeeded = 0;
    let failed = 0;
    for (const userId of targets) {
      try {
        await linkGroup(userId, groupId);
        succeeded++;
      } catch {
        failed++;
      }
      setProgress({ groupId, done: succeeded + failed, total: targets.length });
    }
    setProgress(null);
    queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
    if (failed === 0) {
      toast.success(t('users.bulkGroupLinked', { count: succeeded, skipped }));
      setOpen(false);
    } else {
      toast.warning(t('users.allGroupsLinkedPartial', { succeeded, failed }));
    }
  };

  const isLoading = loadingGroups || loadingPerms;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!progress) setOpen(o); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1" disabled={disabled}>
          <Layers className="h-4 w-4" />
          {t('users.addGroup')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('users.bulkAddGroup')}</DialogTitle>
          <DialogDescription>
            {t('users.bulkAddGroupDescription', { count: userIds.length })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div>
            <Input
              placeholder={t('users.searchGroups')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-2"
            />
            <ScrollArea className="h-[280px] border rounded-md">
              {filteredGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground p-3">{t('users.noGroupsMatch')}</p>
              ) : (
                <div className="p-2 space-y-1">
                  {filteredGroups.map(group => {
                    const linkedCount = userIds.filter(id =>
                      (permsMap?.groupsByUser[id] ?? []).includes(group.id)
                    ).length;
                    const allLinked = linkedCount === userIds.length;
                    return (
                      <div
                        key={group.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm">{group.name}</span>
                          {linkedCount > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {t('users.bulkGroupAlreadyLinked', { count: linkedCount, total: userIds.length })}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAdd(group.id)}
                          disabled={!!progress || allLinked}
                          className="h-7 gap-1"
                        >
                          {progress?.groupId === group.id ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              {progress.done}/{progress.total}
                            </>
                          ) : (
                            <>
                              <Plus className="h-3.5 w-3.5" />
                              {t('common.add')}
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
